import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { useAddEventMutation } from "src/api/purchasing/events";
import { useGetPaymentMethods } from "src/api/generic";
import { useTranslation } from "react-i18next";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { convertObjectToFormData } from "src/utils";
import { useGetCapitalList } from "src/api/settings/capital";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { authState } from "src/store/auth";

interface Props {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
}

const schema = yup.object({
  mode: yup
    .string()
    .required("required")
    .oneOf(["add", "edit"] as const),
  direction: yup
    .string()
    .oneOf(["to_other_revenue", "from_other_revenue"])
    .required("required"),
  operation: yup.string().required("required"),
  payment_id: yup.string().when("operation", {
    is: (op: string) => ["refund", "advanced", "settlement"].includes(op),
    then: (v) => v.required("required"),
  }),
  capital_id: yup.string().when("operation", {
    is: "owner",
    then: (v) => v.required("required"),
  }),
  amount: yup.number().required("required"),
  date: yup.date().required("required"),
  note: yup.string(),
});

type FormInputs = yup.InferType<typeof schema>;

const OtherRevenueEventPopup = ({ open, handleClose, refetch }: Props) => {
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const [files, setFiles] = useState<any>([]);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const auth = useRecoilValue(authState);
  const company = auth?.user?.company;

  // apis
  const { mutate, isPending } = useAddEventMutation();
  const { data: paymentsData } = useGetPaymentMethods();
  const { data: capitalData } = useGetCapitalList();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
    },
  });

  const direction = watch("direction");
  const operation = watch("operation");

  const getOperations = [
    { value: "owner", label: "Owner" },
    { value: "payment", label: "Payment" },
  ];

  const shouldShowPayment = operation === "payment";

  const onClose = () => {
    handleClose();
    reset({});
  };

  const onSubmit = (data: FormInputs) => {
    // Balance date validation
    if (
      !!company?.start_balance_date &&
      moment(data.date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date
          }`,
        },
      ]);
      return;
    }

    const payload = {
      // missing the other revenue id
      type: "other_revenue",
      operation: data.direction,
      method: data.operation,
      amount: +data.amount,
      date: moment(data.date).format("YYYY-MM-DD"),
      ...(shouldShowPayment &&
        data.direction === "to_other_revenue" && {
          payment_from_id: data.payment_id,
        }),
      ...(shouldShowPayment &&
        data.direction === "from_other_revenue" && {
          payment_to_id: data.payment_id,
        }),
      ...(operation === "owner" && { capital_id: data.capital_id }),
      ...(data.note && { note: data.note }),
    };

    const formData = convertObjectToFormData(payload);

    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    mutate(formData, {
      onSuccess: () => {
        refetch();
        onClose();
      },
      onError: (error) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message:
              error?.data?.errors?.[0].value || generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Stack
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", lg: "537px" },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontSize="20px">{t("otherRevenueEvent")}</Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            {/* Direction */}
            <FormControl size="small" error={!!errors.direction}>
              <Controller
                name="direction"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="to_other_revenue"
                      control={<Radio size="small" />}
                      label={t("receiveOtherRevenue")}
                    />
                    <FormControlLabel
                      value="from_other_revenue"
                      control={<Radio size="small" />}
                      label={t("refundReverseOtherRevenue")}
                    />
                  </RadioGroup>
                )}
              />
              <FormHelperText>{errors.direction?.message}</FormHelperText>
            </FormControl>

            {/* Select other revenue */}

            {/* Operation */}
            {direction && (
              <FormControl size="small" error={!!errors.operation}>
                <Controller
                  name="operation"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      {getOperations.map((op) => (
                        <FormControlLabel
                          key={op.value}
                          value={op.value}
                          control={<Radio size="small" />}
                          label={op.label}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <FormHelperText>{errors.operation?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Payment */}
            {shouldShowPayment && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.payment_id}
                sx={{ mt: 1 }}
              >
                <InputLabel>Payment</InputLabel>
                <Controller
                  name="payment_id"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Payment">
                      {paymentsData?.data?.map((p: any) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.payment_id?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Account (Expenses only) */}
            {operation === "owner" && (
              <FormControl
                fullWidth
                size="small"
                error={!!errors.capital_id}
                sx={{ mt: 1 }}
              >
                <InputLabel id="demo-simple-select-label">
                  {t("owner")}
                </InputLabel>
                <Controller
                  name="capital_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={"owner"}
                    >
                      {capitalData?.data.map(({ id, name }: any) => {
                        return (
                          <MenuItem key={id} value={`${id}`}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {errors.capital_id?.message}
                </FormHelperText>
              </FormControl>
            )}

            {/* Amount */}
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  label={t("amount")}
                  fullWidth
                  sx={{ mt: 1 }}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                />
              )}
            />

            {/* Note */}
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t("notesOptional")}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mt: 1 }}
                />
              )}
            />

            {/* Date */}
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  format={"dd/MM/yyyy"}
                  label={`${t("transactionDateRepeat")}`}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: errors.date?.message,
                      inputProps: {
                        readOnly: true,
                        onKeyDownCapture: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        },
                        onPasteCapture: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        },
                      },
                    },
                  }}
                />
              )}
            />

            <FilePondUploader
              onUpload={(e) => setFiles(e)}
              maxFiles={3}
              maxFileSize={3}
              acceptedFileTypes={["image/*", "application/pdf"]}
            />
          </Stack>

          <Stack direction="row" spacing={2} mt={3}>
            <Button variant="outlined" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isPending}
            >
              Submit
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default OtherRevenueEventPopup;
